// Form templates — reusable form-template list via POST /forms/all (server-side
// pagination). Request body + response fields are confirmed against the backend;
// `status` is the raw `formStatus` lifecycle, and the Status column derives its
// badges from it plus the `currentVersion`/`publishedVersion` ids (see
// formTemplateColumns). Normalization stays isolated in toFormTemplate so any
// drift is a one-file fix (same idiom as datasourceApi).

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export type FormStatus = 'draft' | 'published' | 'suspended';

/** `state[]` filter values — the version-derived states: 'new' (never
 *  published, resolved from a null publishedVersion), 'draft' (working version
 *  ahead of the published one) and 'published'. Lifecycle (active/suspended)
 *  travels separately in `status[]`. */
export type FormStateFilter = 'new' | 'draft' | 'published';

/** The Status filter category's pickable lifecycle values (the tab contributes
 *  'active'/'archived' on its own — see FormTemplateQueryArgs.status). */
export type FormStatusFilter = 'active' | 'suspended';

/** The backend `formStatus` lifecycle, carried verbatim on the list row —
 *  distinct from FormStatus, the `state` filter's display values. */
export type FormLifecycleStatus = 'active' | 'suspended' | 'archived';

/** Folder narrowing on the templates list. Only UNASSIGNED goes on the wire:
 *  ALL means omit the param, a specific-folder pick is a bare `folder` ids
 *  array (no folderType — its former CUSTOM member was removed 2026-09-08),
 *  and ASSIGNED is contract-only with no sidebar entry yet. */
export enum FormFolderFilter {
  ALL = 'all',
  ASSIGNED = 'assigned',
  UNASSIGNED = 'unassigned',
}

/** Date-preset filter on the templates list ("Any time" = send nothing). */
export enum FormDateFilter {
  TODAY = 'today',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_90_DAYS = 'last_90_days',
  CUSTOM = 'custom',
}

/** The listing page's tabs. These are tab names, not `status` values — the page's
 *  TAB_STATUS maps them (both 'forms' and 'bookmarks' fetch 'active'; bookmarks
 *  additionally sets the `bookmarked` filter). */
export type FormTab = 'forms' | 'bookmarks' | 'archived';

/** Report-layout thumbnail-size options. */
export enum FormThumbSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

/** Report-layout photo-resolution options. */
export enum FormPhotoResolution {
  Standard = 'standard',
  High = 'high',
}

/** Report-layout page-size options (sent capitalised). */
export enum FormPageSize {
  A4 = 'A4',
  Letter = 'Letter',
  Legal = 'Legal',
}

/** Form text direction (also the list item's `textDirection`). */
export enum FormTextDirection {
  LeftToRight = 'left_to_right',
  RightToLeft = 'right_to_left',
}

/** Backend `orderBy.key` values for the form-templates list. */
export enum FormSortKey {
  CreatedAt = 'createdAt',
  Title = 'title',
  NumberOfEntries = 'numberOfEntries',
  LastEntryDate = 'lastEntryDate',
  LastUpdated = 'updatedAt',
  PublishedDate = 'publishedDate',
  Status = 'formStatus',
}

export interface FormPerson {
  id: string;
  fullName: string;
}

/** A form's folder as the LIST returns it — populated, name only. (The detail
 *  endpoint returns a bare id string instead; separate contracts.) */
export interface FormFolderRef {
  id: string;
  title: string;
}

export interface FormTemplate {
  id: string;
  name: string;
  /** kebab-case lucide name. */
  icon: string;
  /** ISO publish date, or null for unpublished (draft) forms. */
  publishedDate: string | null;
  status: FormLifecycleStatus;
  /** Id of the working (latest) version — always present. */
  currentVersion: string;
  /** Id of the live published version, or null when never published. */
  publishedVersion: string | null;
  /** ISO timestamp of the most recent entry, or null. */
  lastEntry: string | null;
  entries: number | null;
  /** The folder the template is filed in, or null when unfiled. */
  folder: FormFolderRef | null;
  createdBy: FormPerson | null;
  lastUpdatedBy: FormPerson | null;
}

export interface FormTemplatePage {
  items: FormTemplate[];
  total: number;
}

/** Lightweight form for pickers (GET /forms/selection — id, title, icon). */
export interface FormOption {
  id: string;
  name: string;
  /** kebab-case lucide name. */
  icon: string;
}

/** POST /forms/create-form body. `folder` is always sent (null = no folder). */
export interface CreateFormPayload {
  title: string;
  /** The folder to file the new form in, or null for none. */
  folder: string | null;
}

/** PATCH /forms/bookmark body — bookmarks one or many forms in a single call.
 *  `bookmark` is always sent true: the list rows carry no bookmark flag, so the
 *  UI has no state to toggle off from (confirmed one-way for now). */
export interface BookmarkFormsPayload {
  formIds: string[];
  bookmark: boolean;
}

/** PATCH /forms/set-test-status body — marks one or many forms as under test.
 *  Same multi-id shape as BookmarkFormsPayload; the builder always sends one. */
export interface SetTestStatusPayload {
  formIds: string[];
  markAsTest: boolean;
}

/** The four lifecycle verbs behind PATCH /forms/bulk-action (user-confirmed
 *  contract). `restore` undoes `archive`; `active` undoes `suspend`. */
export enum FormBulkAction {
  ARCHIVE = 'archive',
  RESTORE = 'restore',
  SUSPEND = 'suspend',
  ACTIVE = 'active',
}

/** PATCH /forms/bulk-action body — moves one or many forms through the
 *  lifecycle. Same multi-id shape as BookmarkFormsPayload. */
export interface FormBulkActionPayload {
  formIds: string[];
  action: FormBulkAction;
}

/** POST /forms/bulk-delete body — moves one or many forms to Trash
 *  (user-confirmed contract). Same multi-id shape as FormBulkActionPayload. */
export interface DeleteFormsPayload {
  formIds: string[];
}

/** PATCH /forms/update-forms-folder body — files one or many forms into a folder.
 *  `folder` is null for "no folder", the same convention as UpdateFormPayload.
 *  Note the id key is `forms` here, not `formIds` as the bookmark and test-status
 *  bodies use. */
export interface UpdateFormsFolderPayload {
  folder: string | null;
  forms: string[];
}

/** One element in a POST /forms/import-form body — the Export JSON element
 *  contract: no `_id`/`elementGlobalId` (user-confirmed omitted; the backend
 *  mints fresh ids from `uniqueIdToReplace`), `properties` stays a stringified
 *  blob. */
export interface ImportFormElement {
  elementType: string;
  position: number;
  pageNumber: number;
  sectionNumber: number;
  properties: string;
  uniqueIdToReplace: string;
  triggers?: FormElementTriggers;
  triggerFlowReferenceId?: string | null;
}

/** POST /forms/import-form body — creates a form from an exported design file.
 *  `textDirection` is excluded for now (user-confirmed). */
export interface ImportFormPayload {
  title: string;
  description: string;
  icon: string;
  submitButtonColor: string;
  elements: ImportFormElement[];
}

/** Intro-screen config; `null` on the form when the intro screen is disabled. */
export interface FormInstructions {
  isEnabled: boolean;
  description: string;
  media: string[];
}

/** PATCH /forms/update-form/:id body — the Setup tab's settings. `workflow`,
 *  `workflowVersion` and `folder` are ALWAYS sent (null = detached / no
 *  folder): an omitted key leaves the backend's existing value in place, so an
 *  optional key could never clear the field. Nullable here matches the detail
 *  shape, which already reads them back as `string | null`.
 *  `formInstructions` is null when the intro screen is off. */
export interface UpdateFormPayload {
  title: string;
  description: string;
  icon: string;
  submitButtonColor: string;
  /** The approval workflow's id, or null when none is attached. */
  workflow: string | null;
  /** The chosen version's id — null whenever `workflow` is. */
  workflowVersion: string | null;
  /** The owning folder's id, or null when the form sits in no folder. */
  folder: string | null;
  formInstructions: FormInstructions | null;
}

/** PATCH /forms/update-report-layout/:id body — the Report-layout tab's settings.
 *  Also the shape of `reportLayout` on the form detail (save and read match). */
export interface UpdateReportLayoutPayload {
  textDirection: FormTextDirection;
  showLogo: boolean;
  showQRCode: boolean;
  pageSize: FormPageSize;
  thumbSize: FormThumbSize;
  photoResolution: FormPhotoResolution;
  mediaSummary: boolean;
  workflowSummary: boolean;
  showUnansweredQuestions: boolean;
  hideQuestionNumbering: boolean;
  enableFooter: boolean;
  footerText: string;
}

/** External-report (API export) data types — backend DataTypeValues verbatim. */
export const API_EXPORT_DATA_TYPES = ['string', 'number', 'date_time', 'boolean'] as const;
export type ApiExportDataType = (typeof API_EXPORT_DATA_TYPES)[number];

/** What a binding maps from: a form field, a fixed value, or submission metadata. */
export type ApiExportBindingType = 'element' | 'custom' | 'system';

/** Backend ExportRequestAuthType. bearer/custom both carry a pasted token in
 *  `authValue`; api_auth fetches one from the auth endpoint fields. */
export type ApiExportAuthType = 'bearer' | 'api_auth' | 'custom';

/** Backend ExportRequestMethod — lowercase on the wire; the UI labels render uppercase. */
export type ApiExportMethod = 'get' | 'post';

export interface ApiExportAuth {
  authType: ApiExportAuthType;
  /** Bearer/custom token — '' on the api_auth variant. */
  authValue: string;
  /** api_auth only — '' otherwise, likewise the fields below. */
  authApiUrl: string;
  authApiMethod: ApiExportMethod;
  parameters: { client_name: string; client_id: string; client_secret: string };
  responseAuthKeyName: string;
}

export interface ApiExportBinding {
  /** External column key the value lands in. */
  key: string;
  dataType: ApiExportDataType;
  type: ApiExportBindingType;
  /** The fixed value — only when type === 'custom'. */
  customValue?: string;
}

export interface ApiExportMediaBinding {
  key: string;
  /** Always 'string' — media exports as URLs. */
  dataType: ApiExportDataType;
  type: 'element';
  allowMultiple: boolean;
}

/** PATCH /forms/update-api-export/:id body — also the saved shape inside the
 *  detail's `apiExportSetting[]` (save and read match, the reportLayout idiom). */
export interface UpdateApiExportPayload {
  isEnabled: boolean;
  apiEndpoint: string;
  apiMethod: ApiExportMethod;
  auth: ApiExportAuth;
  /** Keyed by fieldId (element), system-field id (system) or column key (custom). */
  bindingList: Record<string, ApiExportBinding>;
  mediaBindingList: Record<string, ApiExportMediaBinding>;
}

/** Trigger action kinds of the conditional-logic contract (legacy-identical). */
export type FormTriggerType =
  | 'ask_a_question'
  | 'require_picture'
  | 'require_video'
  | 'require_note'
  | 'user_page';

/** One action entry of a grouped trigger. `ask_a_question` carries the CHILD
 *  element's ids (elementGlobalId null until the child is first saved, plus the
 *  triggerFlowReferenceId uuid also stamped on the child's own row so the
 *  backend can link the two); `user_page` carries the target PAGE's ids the
 *  same way. The bare kinds carry `type` alone. */
export interface FormTriggerTypeEntry {
  type: FormTriggerType;
  elementGlobalId?: string | null;
  triggerFlowReferenceId?: string | null;
}

/** An element row's conditional logic, grouped for the wire:
 *  matcher → matchingValue (the picked option's VALUE) → action entries.
 *  This builder only ever WRITES the `is` matcher, but the read side stays
 *  matcher-keyed so a legacy matcher can never explode a parse. */
export type FormElementTriggers = Record<string, Record<string, FormTriggerTypeEntry[]>>;

/** One row of PATCH /forms/update-elements — the builder's flattened element
 *  list. `properties` is the element's property object JSON-stringified.
 *  `_id`/`elementGlobalId` are echoed back on elements that came from the saved
 *  design and sent as **null** on builder-created ones — always present on the
 *  wire, matching the builder model's own `string | null`. Those
 *  builder-created rows also carry `uniqueIdToReplace` (omitted, not nulled,
 *  when absent) — a temp id the backend swaps for the real `elementGlobalId`,
 *  so dataSource bindings written against a never-saved field can be
 *  rewritten. */
export interface FormElementPayload {
  _id: string | null;
  uniqueIdToReplace?: string;
  elementGlobalId: string | null;
  elementType: string;
  /** Global 1-based index across the flattened element array. */
  position: number;
  /** 1-based ordinal of the page element this row starts/sits under. */
  pageNumber: number;
  /** 1-based ordinal of the section this row starts/sits under, restarting
   *  each page; 0 before any section (and on page rows). */
  sectionNumber: number;
  properties: string;
  /** Conditional logic on this row — omitted entirely (never an empty object,
   *  matching the legacy serializer) when the element has no complete rules. */
  triggers?: FormElementTriggers;
  /** Trigger-linkage uuid: sent on every PAGE row (a never-saved page must be
   *  targetable by a `user_page` action) and on ask_a_question CHILD rows
   *  (same value as the parent's type entry); omitted everywhere else. */
  triggerFlowReferenceId?: string | null;
}

/** PATCH /forms/update-elements body. */
export interface UpdateFormElementsPayload {
  formId: string;
  elements: FormElementPayload[];
}

/** One saved element of the form's current design — `properties` stays the
 *  backend's JSON string; parsing/grouping into builder pages happens in the
 *  builder's serialization layer (builderPayload.ts). A saved element **always**
 *  carries both ids (confirmed) — hence no nulls, and the builder can seed them
 *  straight through. */
export interface FormDesignElement {
  _id: string;
  elementGlobalId: string;
  elementType: string;
  position: number;
  pageNumber: number;
  sectionNumber: number;
  properties: string;
  /** Conditional logic on this row, grouped — absent when the row has none.
   *  The seed resolves ask_a_question/user_page references by elementGlobalId. */
  triggers?: FormElementTriggers;
  /** Echoed linkage uuid — not needed on load (references resolve by
   *  elementGlobalId), passed through so a re-save can't drop it. */
  triggerFlowReferenceId?: string | null;
}

/** Lifecycle of the detail's current design version — drives Publish. */
export enum FormVersionState {
  Draft = 'draft',
  Published = 'published',
}

/** The form detail's `design` block — the saved current version's elements. */
export interface FormDesign {
  /** The version's id (raw `design._id`, same value as its `versions[]` row). */
  id: string;
  version: number;
  versionState: FormVersionState;
  /** Sorted by `position`. */
  elements: FormDesignElement[];
}

/** One row of the Form-versions history (read-only view). */
export interface FormVersionEntry {
  id: string;
  version: number;
  updatedAt: string;
  /** Sourced from the API's `lastUpdatedBy` (it also exposes `publishedBy`,
   *  the publish-side actor, which the UI doesn't surface). */
  updatedBy: FormPerson | null;
}

/** GET /forms/detail/:id — its own contract, deliberately NOT extending
 *  `FormTemplate`: the detail returns no `publishedVersion` / `currentVersion` /
 *  `lastEntryDate`, so `status` is derived from the current version's state and
 *  there is no `lastEntry` at all. (`formInstructions` is not modelled yet —
 *  shape pending.) */
export interface FormDetail {
  name: string;
  description: string;
  icon: string;
  submitButtonColor: string;
  workflow: string | null;
  workflowVersion: string | null;
  /** The owning folder's id, or null when the form sits in no folder. */
  folder: string | null;
  reportLayout: UpdateReportLayoutPayload | null;
  /** Saved External-report (API export) settings, or null when never saved. */
  apiExport: UpdateApiExportPayload | null;
  /** ISO timestamp of the last update. */
  updatedAt: string;
  lastUpdatedBy: FormPerson | null;
  /** Test mode — the form is open for test submissions on the mobile app.
   *  Raw `isTestingMode`; set through PATCH /forms/set-test-status. */
  testing: boolean;
  /** Version history, newest first. */
  versions: FormVersionEntry[];
  /** The current version's design — always present (a form gets v1 at creation). */
  design: FormDesign;
}

/** POST /forms/all body — `status` (the tab's lifecycle), `page` and `limit`
 *  are required; `state` (the New/Draft/Published filter), `search`, `orderBy`,
 *  `bookmarked` and `published` are optional and omitted (undefined) when unset. */
export interface FormTemplateQueryArgs {
  /** REQUIRED lifecycle array — always carries the tab's value ('active' /
   *  'archived') plus the Status filter's Active/Suspended picks, deduped
   *  (the People page's userStatus idiom). */
  status: FormLifecycleStatus[];
  page: number;
  limit: number;
  /** Restrict to bookmarked forms. Omitted (not false) when unset — sending
   *  false would read as "only non-bookmarked" and hide bookmarked rows from
   *  the default list. (Note the LIST filter is `bookmarked`, while the
   *  bookmark mutation's own body key is `bookmark`.) */
  bookmarked?: boolean;
  /** Restrict to published forms. Only Reports sends it (a report is a form that
   *  has been published and can collect submissions); the Forms list omits it so
   *  drafts still show. */
  published?: boolean;
  state?: FormStateFilter[];
  createdBy?: string[];
  folderType?: FormFolderFilter;
  /** Folder ids (forms sidebar / Reports filter) — self-sufficient, no
   *  folderType accompanies them. */
  folder?: string[];
  publishedDate?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  publishedDateStart?: string;
  publishedDateEnd?: string;
  lastUpdated?: FormDateFilter;
  lastUpdatedStart?: string;
  lastUpdatedEnd?: string;
  lastEntry?: FormDateFilter;
  lastEntryStart?: string;
  lastEntryEnd?: string;
  search?: string;
  orderBy?: { key: FormSortKey; type: 'asc' | 'desc' };
}

// ---- raw shapes (per the confirmed response) ----
interface ApiFormPerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
}
interface ApiFormFolderRef {
  _id: string;
  title: string;
}
interface ApiFormTemplate {
  _id: string;
  title: string;
  icon: string;
  numberOfEntries?: number | null;
  /** Populated folder document (narrowed to what the table renders); absent or
   *  null when the template is unfiled. */
  folder?: ApiFormFolderRef | null;
  /** Bare version id, or null/absent when the form was never published. */
  publishedVersion?: string | null;
  publishedDate?: string | null;
  /** Bare version id of the working version — always present (confirmed). */
  currentVersion: string;
  lastEntryDate?: string | null;
  /** Lifecycle — the row's `status` verbatim; the Status column derives its
   *  draft/published/new badges from the version ids, not from this. */
  formStatus: FormLifecycleStatus;
  textDirection?: string | null;
  createdAt: string;
  createdBy?: ApiFormPerson | null;
  lastUpdatedBy?: ApiFormPerson | null;
}
type ApiFormTemplatePage =
  | { items?: ApiFormTemplate[]; totalRecords?: number; total?: number }
  | ApiFormTemplate[];

/** Raw `reportLayout` block — loose (strings for the enums) so drift stays here. */
interface ApiReportLayout {
  textDirection?: string | null;
  showLogo?: boolean | null;
  showQRCode?: boolean | null;
  pageSize?: string | null;
  thumbSize?: string | null;
  photoResolution?: string | null;
  mediaSummary?: boolean | null;
  workflowSummary?: boolean | null;
  showUnansweredQuestions?: boolean | null;
  hideQuestionNumbering?: boolean | null;
  enableFooter?: boolean | null;
  footerText?: string | null;
}
/** Raw design element — loose so drift stays here. */
interface ApiFormDesignElement {
  _id: string;
  elementGlobalId: string;
  elementType: string;
  position: number;
  pageNumber: number;
  sectionNumber: number;
  properties: string;
  triggers?: FormElementTriggers | null;
  triggerFlowReferenceId?: string | null;
}
/** Raw GET /forms/detail/:id — an independent contract, NOT a superset of the
 *  list row: it carries no `publishedVersion` / `currentVersion` /
 *  `lastEntryDate`. Confirmed against the real response. */
interface ApiFormDetail {
  _id: string;
  title: string;
  description: string;
  icon: string;
  submitButtonColor: string;
  textDirection: string;
  numberOfEntries: number;
  /** Lifecycle (active/archived/suspended) — not the draft/published display state. */
  formStatus: string;
  createdAt: string;
  updatedAt: string;
  createdBy: ApiFormPerson;
  lastUpdatedBy: ApiFormPerson;
  /** null when no approval workflow is attached. */
  workflow: string | null;
  workflowVersion: string | null;
  /** The owning folder's id — null when the form is unassigned. */
  folder: string | null;
  /** Open for test submissions on the mobile app (PATCH /forms/set-test-status). */
  isTestingMode: boolean;
  /** The saved layout, or null when none was ever saved. */
  reportLayout: ApiReportLayout | null;
  /** Always present — a form gets its first version at creation. */
  design: {
    _id: string;
    version: number;
    versionState: FormVersionState;
    elements: ApiFormDesignElement[];
  };
  versions: {
    _id: string;
    version: number;
    updatedAt: string;
    /** Who last edited this version — pairs with `updatedAt`. */
    lastUpdatedBy: ApiFormPerson;
    /** Who published it, and when — set only once published. */
    publishedBy?: ApiFormPerson;
    publishedDate?: string;
  }[];
  /** External-report settings, or null when never saved. */
  apiExportSetting: UpdateApiExportPayload | null;
  /** Returned by the API, unused by the client. */
  metadata: unknown[];
  bookmarkedUsers: unknown[];
  /** Inconsistently present on this endpoint — unused; prefer the published
   *  version's `versions[].publishedDate`. */
  publishedDate?: string;
}

const toFolderRef = (f?: ApiFormFolderRef | null): FormFolderRef | null =>
  f ? { id: f._id, title: f.title } : null;

const toPerson = (p?: ApiFormPerson | null): FormPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toFormTemplate = (f: ApiFormTemplate): FormTemplate => ({
  id: f._id,
  name: f.title,
  icon: f.icon,
  publishedDate: f.publishedDate ?? null,
  status: f.formStatus,
  currentVersion: f.currentVersion,
  publishedVersion: f.publishedVersion ?? null,
  lastEntry: f.lastEntryDate ?? null,
  entries: f.numberOfEntries ?? null,
  folder: toFolderRef(f.folder),
  createdBy: toPerson(f.createdBy),
  lastUpdatedBy: toPerson(f.lastUpdatedBy),
});

// Coerce the raw report-layout block to the typed settings, falling back to the
// same defaults the tab seeds from when a field is absent.
const toReportLayout = (r: ApiReportLayout): UpdateReportLayoutPayload => ({
  textDirection: (r.textDirection as FormTextDirection) ?? FormTextDirection.LeftToRight,
  showLogo: r.showLogo ?? true,
  showQRCode: r.showQRCode ?? true,
  pageSize: (r.pageSize as FormPageSize) ?? FormPageSize.A4,
  thumbSize: (r.thumbSize as FormThumbSize) ?? FormThumbSize.Small,
  photoResolution: (r.photoResolution as FormPhotoResolution) ?? FormPhotoResolution.Standard,
  mediaSummary: r.mediaSummary ?? false,
  workflowSummary: r.workflowSummary ?? false,
  showUnansweredQuestions: r.showUnansweredQuestions ?? false,
  hideQuestionNumbering: r.hideQuestionNumbering ?? false,
  enableFooter: r.enableFooter ?? false,
  footerText: r.footerText ?? '',
});

// The saved design block — elements sorted by position (rows without an
// elementType are dropped; they can't be rebuilt into a builder element).
const toDesign = (d: ApiFormDetail['design']): FormDesign => ({
  id: d._id,
  version: d.version,
  versionState: d.versionState,
  elements: d.elements
    .filter((e) => e.elementType)
    .map((e) => ({
      _id: e._id,
      elementGlobalId: e.elementGlobalId,
      elementType: e.elementType,
      position: e.position,
      pageNumber: e.pageNumber,
      sectionNumber: e.sectionNumber,
      properties: e.properties,
      // Dropping these on load would DESTROY the form's conditional logic on
      // the next save (update-elements replaces the whole element list).
      ...(e.triggers && Object.keys(e.triggers).length ? { triggers: e.triggers } : {}),
      ...(e.triggerFlowReferenceId ? { triggerFlowReferenceId: e.triggerFlowReferenceId } : {}),
    }))
    .sort((a, b) => a.position - b.position),
});

// Version history rows, newest first.
const toVersions = (f: ApiFormDetail): FormVersionEntry[] =>
  [...f.versions]
    .map((v) => ({
      id: v._id,
      version: v.version,
      updatedAt: v.updatedAt,
      updatedBy: toPerson(v.lastUpdatedBy),
    }))
    .sort((a, b) => b.version - a.version);

// Mapped field-by-field (not spread from toFormTemplate) — the two endpoints are
// separate contracts — and narrowed to what the UI consumes. No defensive
// fallbacks: the raw shape is confirmed.
const toFormDetail = (f: ApiFormDetail): FormDetail => ({
  name: f.title,
  description: f.description,
  icon: f.icon,
  submitButtonColor: f.submitButtonColor,
  workflow: f.workflow,
  workflowVersion: f.workflowVersion,
  folder: f.folder,
  reportLayout: f.reportLayout ? toReportLayout(f.reportLayout) : null,
  apiExport: f.apiExportSetting,
  updatedAt: f.updatedAt,
  lastUpdatedBy: toPerson(f.lastUpdatedBy),
  testing: f.isTestingMode,
  versions: toVersions(f),
  design: toDesign(f.design),
});

export const formsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFormTemplates: build.query<FormTemplatePage, FormTemplateQueryArgs>({
      query: (body) => ({ ...ROUTES.forms.list, data: body }),
      transformResponse: (res: ApiFormTemplatePage) => {
        const items = Array.isArray(res) ? res : (res?.items ?? []);
        const total = Array.isArray(res)
          ? res.length
          : (res?.totalRecords ?? res?.total ?? items.length);
        return { items: items.map(toFormTemplate), total };
      },
      providesTags: [{ type: 'Form', id: 'LIST' }],
    }),
    // Lightweight form picker source (GET /forms/selection) — id, title, icon.
    getFormsSelection: build.query<FormOption[], void>({
      query: () => ({ ...ROUTES.forms.selection }),
      transformResponse: (res: { items?: Array<{ _id: string; title: string; icon: string }> }) =>
        (res?.items ?? []).map((f) => ({ id: f._id, name: f.title, icon: f.icon })),
      providesTags: [{ type: 'Form', id: 'SELECTION' }],
    }),
    // Creating a form into a folder changes that folder's linkedIds, which the
    // sidebar counts derive from — invalidate the folder list too.
    createForm: build.mutation<{ formId?: string }, CreateFormPayload>({
      query: (body) => ({ ...ROUTES.forms.create, data: body }),
      invalidatesTags: [
        { type: 'Form', id: 'LIST' },
        { type: 'FormFolder', id: 'LIST' },
      ],
    }),
    // GET /forms/detail/:id — a superset of the list item; toFormDetail maps the
    // shared list fields plus the Setup/Report-layout settings the drawer seeds.
    getFormDetail: build.query<FormDetail, string>({
      query: (id) => ({ ...ROUTES.forms.detail(id) }),
      transformResponse: (res: ApiFormDetail) => toFormDetail(res),
      providesTags: (_r, _e, id) => [{ type: 'Form', id }],
    }),
    // The Setup tab also carries the form's folder, so a save can move it between
    // folders — invalidate the folder list too, or the sidebar's linkedIds-derived
    // counts go stale.
    updateForm: build.mutation<unknown, { id: string; body: UpdateFormPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.forms.update(id), data: body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Form', id },
        { type: 'Form', id: 'LIST' },
        { type: 'FormFolder', id: 'LIST' },
      ],
    }),
    updateReportLayout: build.mutation<unknown, { id: string; body: UpdateReportLayoutPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.forms.updateReportLayout(id), data: body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Form', id },
        { type: 'Form', id: 'LIST' },
      ],
    }),
    updateApiExport: build.mutation<unknown, { id: string; body: UpdateApiExportPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.forms.updateApiExport(id), data: body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Form', id },
        { type: 'Form', id: 'LIST' },
      ],
    }),
    // Save bumps the form's version/updatedAt, shown in both the detail and the
    // list — invalidate both.
    updateFormElements: build.mutation<unknown, UpdateFormElementsPayload>({
      query: (body) => ({ ...ROUTES.forms.updateElements, data: body }),
      invalidatesTags: (_r, _e, { formId }) => [
        { type: 'Form', id: formId },
        { type: 'Form', id: 'LIST' },
      ],
    }),
    // Publish flips the current draft version live — status/versionState change
    // in both the detail and the list.
    publishForm: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.forms.publish(id) }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Form', id },
        { type: 'Form', id: 'LIST' },
      ],
    }),
    // Bookmarking doesn't change any rendered list cell today, but it does move
    // forms in/out of the (not yet built) Bookmarks tab — invalidate the list.
    bookmarkForms: build.mutation<unknown, BookmarkFormsPayload>({
      query: (body) => ({ ...ROUTES.forms.bookmark, data: body }),
      invalidatesTags: [{ type: 'Form', id: 'LIST' }],
    }),
    // Test mode is read back from the DETAIL (`isTestingMode`), so invalidate the
    // per-form tag and let the refetch settle the flag — the builder's menu label
    // follows it. The canvas is safe: re-seeding is guarded by seededRef.
    // Moving forms changes the folders' own linkedIds, which the sidebar counts
    // derive from — invalidate the folder list too, same as updateForm does.
    updateFormsFolder: build.mutation<unknown, UpdateFormsFolderPayload>({
      query: (body) => ({ ...ROUTES.forms.updateFormsFolder, data: body }),
      invalidatesTags: (_r, _e, body) => [
        ...body.forms.map((id) => ({ type: 'Form' as const, id })),
        { type: 'Form' as const, id: 'LIST' },
        { type: 'FormFolder' as const, id: 'LIST' },
      ],
    }),
    setTestStatus: build.mutation<unknown, SetTestStatusPayload>({
      query: (body) => ({ ...ROUTES.forms.setTestStatus, data: body }),
      invalidatesTags: (_r, _e, body) => [
        ...body.formIds.map((id) => ({ type: 'Form' as const, id })),
        { type: 'Form' as const, id: 'LIST' },
      ],
    }),
    // Lifecycle moves rewrite each form's `formStatus`, which the list rows and
    // the builder detail both render — invalidate per-id and the list, so the
    // refetch moves rows across the Forms/Archived tabs.
    bulkActionForms: build.mutation<unknown, FormBulkActionPayload>({
      query: (body) => ({ ...ROUTES.forms.bulkAction, data: body }),
      invalidatesTags: (_r, _e, body) => [
        ...body.formIds.map((id) => ({ type: 'Form' as const, id })),
        { type: 'Form' as const, id: 'LIST' },
      ],
    }),
    // LIST only — unlike bulkActionForms there is no per-id detail left to
    // refresh, because the rows no longer exist (the inspections bulk-delete
    // idiom). The forms land in Trash, so nothing here is permanent.
    deleteForms: build.mutation<unknown, DeleteFormsPayload>({
      query: (body) => ({ ...ROUTES.forms.bulkDelete, data: body }),
      invalidatesTags: [{ type: 'Form', id: 'LIST' }],
    }),
    // Queues a server-side CSV export of this form's design — a job, not a
    // download: nothing comes back to consume, so success just means "started"
    // (the file lands through the notifications bell). No invalidatesTags on
    // purpose: an export reads the form, it changes nothing.
    exportFormDesign: build.mutation<unknown, string>({
      query: (formId) => ({ ...ROUTES.exportManager.exportFormDesign, data: { formId } }),
    }),
    // Queues a server-side export of form templates — same job family as the
    // design export above: nothing comes back to consume, nothing invalidated.
    // [] exports the whole listing.
    exportForms: build.mutation<unknown, string[]>({
      query: (formIds) => ({ ...ROUTES.exportManager.exportForms, data: { formIds } }),
    }),
    // Creates a form from an exported design file — the payload IS the Export
    // JSON shape (no textDirection, no element _id/elementGlobalId; the backend
    // mints ids from uniqueIdToReplace). Unlike the exports this CREATES a row,
    // so the list refetches.
    importForm: build.mutation<unknown, ImportFormPayload>({
      query: (body) => ({ ...ROUTES.forms.importForm, data: body }),
      invalidatesTags: [{ type: 'Form', id: 'LIST' }],
    }),
    // The copy inherits the source's folder, so the sidebar's linkedIds counts
    // move too — invalidate FormFolder/LIST alongside the list (createForm's
    // pair). Nothing consumes the response: the list refetch surfaces the copy.
    duplicateForm: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.forms.duplicate(id) }),
      invalidatesTags: [
        { type: 'Form', id: 'LIST' },
        { type: 'FormFolder', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetFormTemplatesQuery,
  useGetFormsSelectionQuery,
  useCreateFormMutation,
  useGetFormDetailQuery,
  useUpdateFormMutation,
  useUpdateReportLayoutMutation,
  useUpdateApiExportMutation,
  useUpdateFormElementsMutation,
  usePublishFormMutation,
  useBookmarkFormsMutation,
  useSetTestStatusMutation,
  useBulkActionFormsMutation,
  useDeleteFormsMutation,
  useUpdateFormsFolderMutation,
  useExportFormDesignMutation,
  useExportFormsMutation,
  useImportFormMutation,
  useDuplicateFormMutation,
} = formsApi;
